import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
} from 'react';
import cytoscape from 'cytoscape';
import type { Core, ElementDefinition, EventObject } from 'cytoscape';
// @ts-expect-error no types ship with the package
import fcose from 'cytoscape-fcose';
import type { GraphNode, KnowledgeGraph } from '../../api/knowledge';
import { DomainCard } from './DomainCard';
import { NodeTooltip } from './NodeTooltip';
import { deriveDomainInfo, nodeSizeForQuality } from './domain';
import './GraphView.css';

let fcoseRegistered = false;
if (!fcoseRegistered) {
  cytoscape.use(fcose);
  fcoseRegistered = true;
}

interface Props {
  graph: KnowledgeGraph;
  filters?: GraphFilters;
  compact?: boolean;
  onNodeSelect?: (node: GraphNode | null) => void;
}

export interface GraphFilters {
  mode: 'all' | 'mature' | 'emergent';
  minQuality: number;
  edgeTypes: Set<string>;
}

interface TooltipState {
  node: GraphNode;
  x: number;
  y: number;
}

interface DomainAnchor {
  name: string;
  color: string;
  count: number;
  x: number;
  y: number;
}

const DOMAIN_PARENT_PREFIX = '__domain__';

const STOPWORDS = new Set([
  // 'argv' and 'argv2' stay: pages written before the migration still
  // name them, and dropping them would surface "argv" as a graph node.
  // 'orchd' joins them for the pages written after it.
  'about', 'after', 'again', 'agent', 'argv', 'argv2', 'between', 'document',
  'orchd', 'orchd2',
  'every', 'first', 'great', 'guide', 'have', 'into', 'machinai', 'noting',
  'notes', 'other', 'overview', 'reference', 'tutorial', 'using',
  'their', 'there', 'these', 'this', 'those', 'with', 'would',
]);

function hexToRgba(color: string, alpha: number): string {
  if (color.startsWith('rgba')) {
    return color.replace(/,\s*[\d.]+\s*\)$/, `,${alpha})`);
  }
  if (color.startsWith('rgb(')) {
    return color.replace('rgb(', 'rgba(').replace(')', `,${alpha})`);
  }
  if (color.startsWith('#')) {
    const hex = color.replace('#', '');
    const bigint = parseInt(
      hex.length === 3
        ? hex.split('').map((c) => c + c).join('')
        : hex,
      16,
    );
    const r = (bigint >> 16) & 255;
    const g = (bigint >> 8) & 255;
    const b = bigint & 255;
    return `rgba(${r},${g},${b},${alpha})`;
  }
  return color;
}

export function GraphView({ graph, filters, compact = false, onNodeSelect }: Props) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const cyRef = useRef<Core | null>(null);
  const [tooltip, setTooltip] = useState<TooltipState | null>(null);
  const [anchors, setAnchors] = useState<DomainAnchor[]>([]);

  const domainInfo = useMemo(() => deriveDomainInfo(graph.nodes), [graph.nodes]);

  const filtered = useMemo(
    () => applyFilters(graph, filters),
    [graph, filters],
  );

  const elements = useMemo<ElementDefinition[]>(() => {
    const els: ElementDefinition[] = [];

    const activeDomains = new Set<string>();
    for (const n of filtered.nodes) activeDomains.add(n.domain || 'unknown');

    // Per-page token bag for similarity scoring. Topic tokens are
    // namespaced with `__topic:` so the score function can weight them
    // higher than incidental title words — topics are owner/agent-curated
    // semantic tags and carry real cross-domain signal.
    const tokensByNode = new Map<string, Set<string>>();
    for (const n of filtered.nodes) {
      const bag = new Set<string>();
      for (const t of n.topics) {
        const norm = t.toLowerCase().trim();
        if (norm.length > 2) bag.add(`__topic:${norm}`);
      }
      for (const word of n.title.toLowerCase().split(/[^a-z0-9]+/)) {
        if (word.length > 3 && !STOPWORDS.has(word)) bag.add(word);
      }
      if (n.subdomain) bag.add(`__sub:${n.subdomain.toLowerCase()}`);
      tokensByNode.set(n.id, bag);
    }

    // ----- Synthesize page-to-page edges from similarity -----
    // Within-domain: keep top INTRA_K matches per node.
    // Cross-domain: only retain edges with score >= CROSS_THRESHOLD.
    //
    // Token weights:
    //   shared subdomain → +SUBDOMAIN_BONUS (intra-domain anchor)
    //   shared topic     → +TOPIC_WEIGHT   (curated semantic signal)
    //   shared title word → +1             (incidental signal)
    //
    // Two shared topics alone clear the cross-domain threshold; one topic
    // plus two title words does not. This rewards real overlap over
    // accidental word-matching.
    const INTRA_K = 3;
    const CROSS_THRESHOLD = 4;
    const SUBDOMAIN_BONUS = 4;
    const TOPIC_WEIGHT = 2;
    const seen = new Set<string>();
    const synthEdges: Array<{
      source: string;
      target: string;
      score: number;
      crossDomain: boolean;
    }> = [];
    const degree = new Map<string, number>();
    const bumpDegree = (id: string) =>
      degree.set(id, (degree.get(id) ?? 0) + 1);

    const score = (a: GraphNode, b: GraphNode): number => {
      const ta = tokensByNode.get(a.id)!;
      const tb = tokensByNode.get(b.id)!;
      let s = 0;
      for (const t of ta) {
        if (!tb.has(t)) continue;
        if (t.startsWith('__sub:')) s += SUBDOMAIN_BONUS;
        else if (t.startsWith('__topic:')) s += TOPIC_WEIGHT;
        else s += 1;
      }
      return s;
    };

    // Intra-domain top-K
    const byDomain = new Map<string, GraphNode[]>();
    for (const n of filtered.nodes) {
      const dom = n.domain || 'unknown';
      let arr = byDomain.get(dom);
      if (!arr) {
        arr = [];
        byDomain.set(dom, arr);
      }
      arr.push(n);
    }
    for (const [, members] of byDomain) {
      for (const a of members) {
        const ranked: Array<{ b: GraphNode; s: number }> = [];
        for (const b of members) {
          if (a.id === b.id) continue;
          const s = score(a, b);
          if (s > 0) ranked.push({ b, s });
        }
        ranked.sort((x, y) => y.s - x.s);
        for (const { b, s } of ranked.slice(0, INTRA_K)) {
          const key = [a.id, b.id].sort().join('|');
          if (seen.has(key)) continue;
          seen.add(key);
          synthEdges.push({ source: a.id, target: b.id, score: s, crossDomain: false });
          bumpDegree(a.id);
          bumpDegree(b.id);
        }
      }
    }

    // Cross-domain only the strongest pairs (sparse)
    const allNodes = filtered.nodes;
    for (let i = 0; i < allNodes.length; i += 1) {
      for (let j = i + 1; j < allNodes.length; j += 1) {
        const a = allNodes[i]!;
        const b = allNodes[j]!;
        if (a.domain === b.domain) continue;
        const s = score(a, b);
        if (s < CROSS_THRESHOLD) continue;
        const key = [a.id, b.id].sort().join('|');
        if (seen.has(key)) continue;
        seen.add(key);
        synthEdges.push({ source: a.id, target: b.id, score: s, crossDomain: true });
        bumpDegree(a.id);
        bumpDegree(b.id);
      }
    }

    // Real edges always count toward degree (they win ties)
    for (const edge of filtered.edges) {
      bumpDegree(edge.source);
      bumpDegree(edge.target);
    }

    // ----- Parent compound nodes -----
    // The compound itself carries no Cytoscape label — the domain
    // header is rendered as a fixed-pixel HTML overlay (DomainCard)
    // anchored INSIDE the compound's top edge. HTML overlays stay
    // legible at any zoom; Cytoscape labels scale with the viewport
    // and become unreadable when the owner zooms out to inspect
    // cross-area edges.
    for (const name of activeDomains) {
      const info = domainInfo.get(name);
      const color = info?.color ?? 'rgba(255,255,255,0.3)';
      els.push({
        data: {
          id: `${DOMAIN_PARENT_PREFIX}${name}`,
          kind: 'domain',
          domainName: name,
          color,
        },
      });
    }

    // ----- Page nodes (with degree-scaled visual weight) -----
    let maxDeg = 0;
    for (const d of degree.values()) if (d > maxDeg) maxDeg = d;
    const sizeForNode = (n: GraphNode): number => {
      const base = nodeSizeForQuality(n.quality);
      const deg = degree.get(n.id) ?? 0;
      const boost = maxDeg > 0 ? Math.log2(1 + deg) / Math.log2(1 + maxDeg) : 0;
      return base * (1 + boost * 0.7);
    };

    for (const node of filtered.nodes) {
      const info = domainInfo.get(node.domain);
      const color = info?.color ?? 'rgba(255,255,255,0.5)';
      const isEmergent = node.domain === 'emergent';
      const deg = degree.get(node.id) ?? 0;
      const isCore = maxDeg > 0 && deg >= Math.max(4, maxDeg * 0.5);
      els.push({
        data: {
          id: node.id,
          parent: `${DOMAIN_PARENT_PREFIX}${node.domain || 'unknown'}`,
          label: node.title,
          domain: node.domain,
          color,
          size: sizeForNode(node),
          opacityFill: isEmergent ? 0.12 : isCore ? 0.55 : 0.32,
          opacityStroke: isEmergent ? 0.3 : isCore ? 1 : 0.85,
          textOpacity: compact ? 0 : isCore ? 0.95 : 0.7,
          raw: node,
          degree: deg,
          isCore,
        },
      });
    }

    // ----- Real edges (authoritative) -----
    const ids = new Set(filtered.nodes.map((n) => n.id));
    for (let i = 0; i < filtered.edges.length; i += 1) {
      const edge = filtered.edges[i];
      if (!edge) continue;
      if (!ids.has(edge.source) || !ids.has(edge.target)) continue;
      const source = filtered.nodes.find((n) => n.id === edge.source);
      const target = filtered.nodes.find((n) => n.id === edge.target);
      const crossDomain = source && target && source.domain !== target.domain;
      const domainColor = source
        ? domainInfo.get(source.domain)?.color ?? '#ffffff'
        : '#ffffff';
      els.push({
        data: {
          id: `e${i}`,
          source: edge.source,
          target: edge.target,
          kind: 'real',
          type: edge.type,
          color: crossDomain ? 'rgba(255,255,255,0.7)' : domainColor,
          opacity: crossDomain ? 0.55 : 0.6,
          dashed: crossDomain ? '5 4' : '',
          edgeWidth: 2,
        },
      });
    }

    // ----- Synthesized similarity edges (visual scaffolding) -----
    let maxScore = 1;
    for (const s of synthEdges) if (s.score > maxScore) maxScore = s.score;
    for (let i = 0; i < synthEdges.length; i += 1) {
      const e = synthEdges[i]!;
      const norm = e.score / maxScore;
      els.push({
        data: {
          id: `s${i}`,
          source: e.source,
          target: e.target,
          kind: 'similarity',
          color: e.crossDomain
            ? 'rgba(255,255,255,0.18)'
            : 'rgba(255,255,255,0.12)',
          opacity: e.crossDomain ? 0.35 + norm * 0.3 : 0.18 + norm * 0.4,
          edgeWidth: e.crossDomain ? 0.8 + norm * 1.2 : 0.6 + norm * 1.4,
        },
      });
    }

    return els;
  }, [filtered, domainInfo, compact]);

  useEffect(() => {
    if (!containerRef.current) return;

    cyRef.current = cytoscape({
      container: containerRef.current,
      elements,
      minZoom: 0.2,
      maxZoom: 3,
      style: [
        {
          selector: 'node[kind = "domain"]',
          style: {
            'background-color': 'data(color)',
            'background-opacity': 0.04,
            'border-color': 'data(color)',
            'border-opacity': 0.35,
            'border-width': 1,
            'corner-radius': '20',
            shape: 'round-rectangle',
            padding: 40,
            // The DomainCard overlay sits FULLY OUTSIDE the
            // compound (its bottom flush with the compound's top
            // edge), so no extra interior padding is needed for
            // the title — children stack with the standard
            // compound padding all around.
            'padding-top': 40,
            label: '',
            'min-width': 160,
            'min-height': 110,
          } as unknown as cytoscape.Css.Node,
        },
        {
          selector: 'node[kind != "domain"], node:childless',
          style: {
            'background-color': 'data(color)',
            'background-opacity': 'data(opacityFill)' as unknown as number,
            'border-color': 'data(color)',
            'border-width': 1.5,
            'border-opacity': 'data(opacityStroke)' as unknown as number,
            width: 'data(size)',
            height: 'data(size)',
            label: 'data(label)',
            'text-opacity': 'data(textOpacity)' as unknown as number,
            color: 'rgba(255,255,255,0.85)',
            'font-size': 10,
            'font-weight': 500,
            'font-family': 'system-ui, -apple-system, sans-serif',
            'text-valign': 'bottom',
            'text-margin-y': 6,
            // The page's background, read when the graph is built: the
            // graph library takes a colour value, not a CSS variable, and
            // `web.brand` sets the background per instance.
            'text-background-color':
              getComputedStyle(document.documentElement)
                .getPropertyValue('--color-background')
                .trim() || '#07070f',
            'text-background-opacity': 0.6,
            'text-background-padding': '3px',
            'text-max-width': '120px',
            'text-wrap': 'ellipsis',
          } as cytoscape.Css.Node,
        },
        {
          selector: 'node:selected',
          style: {
            'border-width': 2.5,
            'border-opacity': 1,
          } as cytoscape.Css.Node,
        },
        {
          selector: 'edge',
          style: {
            width: 'data(edgeWidth)',
            'line-color': 'data(color)',
            'line-opacity': 'data(opacity)' as unknown as number,
            'line-style': (edge: cytoscape.EdgeSingular) =>
              edge.data('dashed') ? 'dashed' : 'solid',
            'line-dash-pattern': [5, 4],
            'curve-style': 'bezier',
            'target-arrow-shape': 'none',
          } as unknown as cytoscape.Css.Edge,
        },
        {
          selector: 'edge[kind = "similarity"]',
          style: {
            'curve-style': 'straight',
          } as unknown as cytoscape.Css.Edge,
        },
        {
          selector: 'edge:active, edge.hover, edge[kind = "real"]:active',
          style: {
            width: 3,
            'line-opacity': 0.95,
          } as cytoscape.Css.Edge,
        },
      ],
      layout: {
        name: 'fcose',
        quality: 'default',
        animate: false,
        randomize: true,
        nodeRepulsion: () => 7000,
        idealEdgeLength: () => 140,
        edgeElasticity: () => 0.35,
        gravity: 0.4,
        gravityRangeCompound: 1.5,
        gravityCompound: 1.2,
        numIter: 2500,
        tilingPaddingVertical: 20,
        tilingPaddingHorizontal: 20,
        nodeDimensionsIncludeLabels: true,
        packComponents: true,
        padding: compact ? 20 : 60,
      } as unknown as cytoscape.LayoutOptions,
    });

    const cy = cyRef.current;

    const handleNodeMouseover = (event: EventObject) => {
      const data = event.target.data();
      if (data.kind === 'domain') return;
      const raw = data.raw as GraphNode;
      const position = event.target.renderedPosition();
      setTooltip({
        node: raw,
        x: position.x,
        y: position.y - event.target.height() / 2,
      });
    };

    const handleNodeMouseout = () => setTooltip(null);

    const handleNodeSelect = (event: EventObject) => {
      const data = event.target.data();
      if (data.kind === 'domain') return;
      onNodeSelect?.(data.raw as GraphNode);
    };

    const handleNodeUnselect = () => onNodeSelect?.(null);

    const handleEdgeMouseover = (event: EventObject) =>
      event.target.addClass('hover');
    const handleEdgeMouseout = (event: EventObject) =>
      event.target.removeClass('hover');

    cy.on('mouseover', 'node', handleNodeMouseover);
    cy.on('mouseout', 'node', handleNodeMouseout);
    cy.on('select', 'node', handleNodeSelect);
    cy.on('unselect', 'node', handleNodeUnselect);
    cy.on('mouseover', 'edge', handleEdgeMouseover);
    cy.on('mouseout', 'edge', handleEdgeMouseout);

    const updateAnchors = () => {
      const next: DomainAnchor[] = [];
      cy.nodes('[kind = "domain"]').forEach((parent) => {
        const name = parent.data('domainName') as string;
        const info = domainInfo.get(name);
        if (!info) return;
        const bb = parent.renderedBoundingBox({ includeLabels: false });
        next.push({
          name,
          color: info.color,
          count: info.count,
          // Center horizontally; anchor at the top edge — the
          // DomainCard's CSS transform sits the card inset just
          // below the border.
          x: (bb.x1 + bb.x2) / 2,
          y: bb.y1,
        });
      });
      setAnchors(next);
    };

    cy.ready(() => {
      updateAnchors();
      cy.on('viewport render', updateAnchors);
    });

    return () => {
      cy.destroy();
      cyRef.current = null;
    };
  }, [elements, domainInfo, compact, onNodeSelect]);

  const zoom = (factor: number) => {
    const cy = cyRef.current;
    if (!cy) return;
    const nextZoom = cy.zoom() * factor;
    cy.zoom({
      level: nextZoom,
      renderedPosition: { x: cy.width() / 2, y: cy.height() / 2 },
    });
  };

  const fit = () => {
    cyRef.current?.fit(undefined, 40);
  };

  const style: CSSProperties = compact ? { minHeight: 160 } : { flex: 1 };

  return (
    <div className={`graph-view${compact ? ' graph-view--compact' : ''}`} style={style}>
      <div ref={containerRef} className="graph-view__canvas" />
      {!compact && (
        <>
          {anchors.map((anchor) => (
            <DomainCard
              key={anchor.name}
              name={anchor.name}
              color={anchor.color}
              count={anchor.count}
              style={{
                left: `${anchor.x}px`,
                top: `${anchor.y}px`,
              }}
            />
          ))}
          {tooltip && (
            <NodeTooltip
              node={tooltip.node}
              color={domainInfo.get(tooltip.node.domain)?.color ?? 'rgba(255,255,255,0.5)'}
              style={{
                left: `${tooltip.x + 14}px`,
                top: `${tooltip.y}px`,
              }}
            />
          )}
          <div className="graph-view__zoom">
            <button type="button" onClick={() => zoom(1.25)} aria-label="Zoom in">
              +
            </button>
            <button type="button" onClick={() => zoom(0.8)} aria-label="Zoom out">
              −
            </button>
            <button
              type="button"
              className="graph-view__zoom-fit"
              onClick={fit}
              aria-label="Fit to view"
              title="Fit"
            >
              ⤢
            </button>
          </div>
          <div className="graph-view__stats">
            <span>{filtered.nodes.length} nodes</span>
            <span className="graph-view__dot">·</span>
            <span>{filtered.edges.length} edges</span>
            <span className="graph-view__dot">·</span>
            <span>{domainInfo.size} domains</span>
          </div>
        </>
      )}
    </div>
  );
}

function applyFilters(graph: KnowledgeGraph, filters?: GraphFilters): KnowledgeGraph {
  if (!filters) return graph;
  const nodes = graph.nodes.filter((n) => {
    if (n.quality < filters.minQuality) return false;
    if (filters.mode === 'mature' && n.domain === 'emergent') return false;
    if (filters.mode === 'emergent' && n.domain !== 'emergent') return false;
    return true;
  });
  const ids = new Set(nodes.map((n) => n.id));
  const edges = graph.edges.filter((e) => {
    if (!ids.has(e.source) || !ids.has(e.target)) return false;
    if (filters.edgeTypes.size > 0 && !filters.edgeTypes.has(e.type)) return false;
    return true;
  });
  return { nodes, edges };
}

export { hexToRgba };
